import type { RemsStatusDto } from "@/api/types";
import remsLogoBig from "@/assets/images/integrations/rems-logo-big.svg";
import remsLogoSmall from "@/assets/images/integrations/rems-logo-small.svg";
import { BrandMark } from "@/design-system/BrandMark";
import { Text } from "@/design-system/Text";
import {
  IntegrationStatus,
  IntegrationStatusFollowUp,
  IntegrationStatusLine,
} from "@/product-patterns/tickets/IntegrationStatus";
import { useTranslation } from "@/translations";

export function RemsStatus({
  bigLogo,
  rems,
  isLoading,
}: {
  bigLogo?: boolean;
  rems: RemsStatusDto;
  isLoading?: boolean;
}): React.ReactNode {
  const presentation = bigLogo ? "prominent" : "compact";

  return (
    <IntegrationStatus
      presentation={presentation}
      brandMark={<BrandMark src={bigLogo ? remsLogoBig : remsLogoSmall} alt="Aareon" size={bigLogo ? "md" : "sm"} />}
    >
      <RemsStatusContent rems={rems} isLoading={isLoading} />
    </IntegrationStatus>
  );
}

function RemsStatusContent({ rems, isLoading }: { rems: RemsStatusDto; isLoading?: boolean }): React.ReactNode {
  const { t } = useTranslation();
  const state = isLoading ? "pending" : rems.state;

  switch (state) {
    case "pending":
      return (
        <>
          {t("page.tickets.rems.state")}{" "}
          <Text variant="inherit" tone="secondary" overflow="normal">
            {t("page.tickets.rems.state.pending")}
          </Text>
        </>
      );
    case "synchronized":
    case "synchronizedAutoClosed":
      return (
        <>
          <IntegrationStatusLine>
            {t("page.tickets.rems.synced")}{" "}
            <Text variant="body-emphasis" tone="success" overflow="normal">
              {rems.externalId}
            </Text>
            {rems.version === "v1" && ` (${t("page.tickets.rems.version.v1")})`}
          </IntegrationStatusLine>
          {state === "synchronizedAutoClosed" && (
            <IntegrationStatusFollowUp>{t("page.tickets.rems.state.auto-closed")}</IntegrationStatusFollowUp>
          )}
        </>
      );
    case "ready":
      return (
        <>
          {t("page.tickets.rems.state")}{" "}
          <Text variant="inherit" tone="success" overflow="normal">
            {t("page.tickets.rems.state.ready")}
          </Text>
        </>
      );
    case "errorNoUserFound":
      return (
        <>
          {t("page.tickets.rems.state")}{" "}
          <Text variant="inherit" tone="danger-secondary" overflow="normal">
            {t("page.tickets.rems.state.user-not-found")}
          </Text>
        </>
      );
    case "errorNoContracts":
      return (
        <>
          {t("page.tickets.rems.state")}{" "}
          <Text variant="inherit" tone="danger-secondary" overflow="normal">
            {t("page.tickets.rems.state.user-no-contracts")}
          </Text>
        </>
      );
    case "errorGeneric":
      return (
        <>
          {t("page.tickets.rems.state")}{" "}
          <Text variant="inherit" tone="danger-secondary" overflow="normal">
            {t("page.tickets.rems.state.error")}
          </Text>
        </>
      );
    case "notStarted":
      return (
        <>
          {t("page.tickets.rems.state")}{" "}
          <Text variant="inherit" tone="secondary" overflow="normal">
            {t("page.tickets.rems.state.not-started")}
          </Text>
        </>
      );
  }
}
